import { Alert } from '@interstate/components/Alert';
import { Box } from '@interstate/components/Box';
import { Button } from '@interstate/components/Button';
import { Typography } from '@interstate/components/Typography';
import { SearchBarErrorContainer, SearchBarErrorDescription, SearchBarErrorLabel } from './SearchAndSelectVehicle.style';
import { useSelector } from 'react-redux';
import { changeVehicleSelectors } from '../../store';

export enum ErrorType {
    NO_RESULTS_FOUND = 'NO_RESULTS_FOUND',
    NO_AGENT_RESULTS_FOUND = 'NO_AGENT_RESULTS_FOUND',
    AGENT_SEARCH_TIMEOUT = 'AGENT_SEARCH_TIMEOUT',
    AGENT_SEARCH_TIMEOUT_REPEATED = 'AGENT_SEARCH_TIMEOUT_REPEATED',
    CURRENT_VEHICLE = 'CURRENT_VEHICLE'
}

const TIMEOUT_ALERT = {
    [ErrorType.AGENT_SEARCH_TIMEOUT]: { type: 'caution', testId: 'search-bar-error-timeout' },
    [ErrorType.AGENT_SEARCH_TIMEOUT_REPEATED]: { type: 'error', testId: 'search-bar-error-timeout-repeated' }
} as const;

const SearchBarError = ({ errorType, onRetry }: { errorType: ErrorType; onRetry?: () => void }) => {
    const isVehicleSearchRequested = useSelector(changeVehicleSelectors.getIsVehicleSearchRequested);

    if (!isVehicleSearchRequested) {
        return null;
    }

    const errorText = {
        [ErrorType.NO_RESULTS_FOUND]: {
            title: 'No results found',
            description:
                'Make sure you have specified the appropriate filters and correct VIN, Stock #, YMMT, or Inventory Type. You may modify the search query or Build the Vehicle above.'
        },
        [ErrorType.NO_AGENT_RESULTS_FOUND]: {
            title: 'No results found from AI-assisted search',
            description:
                "Check to ensure the prompt is accurate and try again. Try adjusting your search criteria by removing filters (click the 'X' in the search bar) or building the vehicle manually."
        },
        [ErrorType.AGENT_SEARCH_TIMEOUT]: {
            title: 'Search is taking longer than expected',
            description: "We couldn't finish your search. Try again."
        },
        [ErrorType.AGENT_SEARCH_TIMEOUT_REPEATED]: {
            title: "We're having trouble searching right now",
            description: 'Try again in a few minutes, or use the filters to browse vehicles.'
        },
        [ErrorType.CURRENT_VEHICLE]: {
            title: 'This is the current vehicle',
            description: 'Make sure you have entered another VIN or Stock #, not from your current vehicle'
        }
    };

    const timeoutAlert = TIMEOUT_ALERT[errorType as keyof typeof TIMEOUT_ALERT];

    if (timeoutAlert) {
        return (
            <Alert
                data-testid={timeoutAlert.testId}
                type={timeoutAlert.type}
                title={errorText[errorType].title}
                sx={{ margin: '16px 0 8px 0' }}
            >
                <Typography variant="body-sm" tag="span">
                    {errorText[errorType].description}
                </Typography>
                {onRetry && (
                    <Box sx={{ marginTop: '8px' }}>
                        <Button
                            buttonStyle={timeoutAlert.type === 'caution' ? 'primary' : 'secondary'}
                            size="small"
                            data-testid="search-bar-error-retry"
                            onClick={onRetry}
                        >
                            Try again
                        </Button>
                    </Box>
                )}
            </Alert>
        );
    }

    return (
        <SearchBarErrorContainer>
            <SearchBarErrorLabel>{errorText[errorType].title}</SearchBarErrorLabel>
            <SearchBarErrorDescription>{errorText[errorType].description}</SearchBarErrorDescription>
        </SearchBarErrorContainer>
    );
};

export default SearchBarError;
